import { memo } from 'react'
import { inkOverlay, readableInk } from '@/utils/color'

interface TableHeaderProps {
  displayName: string
  groupColor: string
  tableId: string
  isSelected: boolean
  isDragging?: boolean
  editTitle: string
  deleteTitle: string
  readOnly?: boolean
  onPointerDown?: (event: React.PointerEvent<HTMLElement>) => void
  onDoubleClick?: () => void
  onEdit?: () => void
  onDelete?: () => void
}

// Edit pencil icon
function EditIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
      <path
        d="M1.5 7.5 L7 2 L8.5 3.5 L3 9 L1 9.5 L1.5 7.5Z"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  )
}

// Trash icon for delete
function TrashIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
      <rect x="1" y="2.5" width="8" height="0.9" rx="0.4" fill="currentColor" />
      <path
        d="M2.5 3.5 L3 8.5 L7 8.5 L7.5 3.5"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M3.8 1.2 L6.2 1.2"
        stroke="currentColor"
        strokeWidth="1"
        strokeLinecap="round"
      />
    </svg>
  )
}

export const TableHeader = memo(function TableHeader({
  displayName,
  groupColor,
  tableId,
  isSelected,
  isDragging = false,
  editTitle,
  deleteTitle,
  readOnly = false,
  onPointerDown,
  onDoubleClick,
  onEdit,
  onDelete,
}: TableHeaderProps) {
  // A cor do grupo vem de um color picker: pode ser tao clara quanto um ciano
  // neon, e ai o branco fixo apagava o nome e os icones do cabecalho.
  const ink = readableInk(groupColor)
  const btnStyle: React.CSSProperties = {
    width: 18,
    height: 18,
    border: 'none',
    borderRadius: 3,
    cursor: 'pointer',
    background: inkOverlay(groupColor, 0.16),
    color: ink,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    flexShrink: 0,
  }

  return (
    <div
      className={`eth${isSelected ? ' eth-selected' : ''}`}
      data-tbl={tableId}
      onPointerDown={onPointerDown}
      onDoubleClick={(event) => {
        if ((event.target as HTMLElement).closest('[data-action]')) return
        onDoubleClick?.()
      }}
      style={{
        ['--eth-btn-hover' as string]: inkOverlay(groupColor, 0.3),
        background: groupColor,
        color: ink,
        fontWeight: 'bold',
        fontSize: 12,
        padding: '6px 9px',
        cursor: readOnly ? 'default' : isDragging ? 'grabbing' : 'grab',
        display: 'flex',
        flexShrink: 0,
        alignItems: 'center',
        gap: 4,
        minHeight: 29,
        touchAction: 'none',
      }}
    >
      <span
        className="eth-name"
        title={displayName}
        style={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {displayName}
      </span>

      {!readOnly ? (
        <span
          className="eth-btns"
          style={{ gap: 2, flexShrink: 0 }}
        >
          <button
            className="eth-btn"
            data-action="edit"
            data-tbl={tableId}
            title={editTitle}
            onClick={(event) => {
              event.stopPropagation()
              onEdit?.()
            }}
            style={btnStyle}
          >
            <EditIcon />
          </button>
          <button
            className="eth-btn del"
            data-action="del"
            data-tbl={tableId}
            title={deleteTitle}
            onClick={(event) => {
              event.stopPropagation()
              onDelete?.()
            }}
            style={btnStyle}
          >
            <TrashIcon />
          </button>
        </span>
      ) : null}
    </div>
  )
})
